import { Component, computed, inject, input } from '@angular/core';
import { AuthService } from '@app/components/login.component';
import {
  injectNotificationsQuery,
  NotificationService
} from '@app/layout/notification.module';
import { ConfirmationService } from 'primeng/api';
import { Observable, map } from 'rxjs';

@Component({
  selector: 'app-homepage-segment',
  styles: [
    `
      .h-80p {
        height: 80% !important;
      }
    `
  ],
  template: `
    <div class="m-2 pb-4 bg-white border-1 border-round-md h-full">
      <h2
        class="p-4 bg-primary text-white flex-none text-center"
        style="font-size: 0.9rem; font-weight: 400;"
      >
        {{ title() }}
      </h2>
      <div class="flex flex-grow flex-column h-80p pr-2 pl-2">
        <ng-content />
      </div>

      <div class="flex gap-4 justify-content-end">
        <p-button
          label="Obriši sve obavijesti"
          [disabled]="isDisabled()"
          (onClick)="clearNotification($event, true)"
        />
        <p-button
          label="Označi sve kao pročitano"
          [disabled]="isDisabled()"
          (onClick)="clearNotification($event, false)"
        />
      </div>
    </div>
  `
})
export class HomepageSegmentComponent {
  public title = input<string>();
  public entityType = input<string | string[]>();

  private notificationService = inject(NotificationService);
  private confirmationService = inject(ConfirmationService);
  private notifications = injectNotificationsQuery(this.entityType);

  public isDisabled = computed(
    () =>
      (this.notifications.data()?.pages.flatMap((page) => page.content)
        .length ?? 0) === 0
  );
  public count = computed(() =>
    this.notifications.data()?.pages.flatMap((page) => page.content)
  );

  public clearNotification(target: unknown, isDelete: boolean) {
    this.confirmationService.confirm({
      target: target as EventTarget,
      message: `Jeste li sigurni da želite ${
        isDelete
          ? 'obrisati sve svoje obavijesti'
          : 'označiti sve obavijesti kao pročitane'
      }?`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const entityType = this.entityType();
        if (Array.isArray(entityType)) {
          for (const type of entityType) {
            this.notificationService.clearNotifications(isDelete, type);
          }
        } else {
          this.notificationService.clearNotifications(isDelete, entityType);
        }
      }
    });
  }
}

@Component({
  styles: [
    `
      .place-around {
        place-content: space-around;
      }
    `
  ],
  template: `
    <main class="w-full">
      <div class="grid place-start w-full">
        <app-homepage-segment
          class="col-6"
          title="PONUDE ZA PREDAJU GRADIVA"
          entityType="earhiv~predaja_gradiva"
          [style]="{ height: '35rem' }"
        >
          <app-notification-items notificationType="earhiv~predaja_gradiva">
            <p>Nemate obavijesti o dostavi gradiva</p>
          </app-notification-items>
        </app-homepage-segment>

        <app-homepage-segment
          class="col-6"
          title="ZAHTJEVI ZA DOSTAVU GRADIVA"
          entityType="earhiv~zahtjev_za_dostavom_gradiva"
          [style]="{ height: '35rem' }"
        >
          <app-notification-items
            notificationType="earhiv~zahtjev_za_dostavom_gradiva"
          >
            <p>Nemate obavijesti o dostavi gradiva</p>
          </app-notification-items>
        </app-homepage-segment>

        <app-homepage-segment
          class="col-6"
          title="PRIJEDLOZI ZA IZLUČIVANJE GRADIVA"
          entityType="en~izlucivanje"
          [style]="{ height: '35rem' }"
        >
          <app-notification-items notificationType="en~izlucivanje">
            <p>Nemate obavijesti o izlučivanju gradiva</p>
          </app-notification-items>
        </app-homepage-segment>

        <app-homepage-segment
          class="col-6"
          title="OBRADA I OPIS GRADIVA"
          [entityType]="[
            'en~jedinica_gradiva',
            'rel~jedinica_gradiva_komentari'
          ]"
          [style]="{ height: '35rem' }"
        >
          <app-notification-items
            [notificationType]="[
              'en~jedinica_gradiva',
              'rel~jedinica_gradiva_komentari'
            ]"
          >
            <p>Nemate obavijesti o obradi i opisu gradiva</p>
          </app-notification-items>
        </app-homepage-segment>

        @if(isUserVoditeljIliKorisnikUredskogPoslovanja()) {
        <app-homepage-segment
          class="col-6"
          title="UREDSKO POSLOVANJE"
          [entityType]="['seup~predmet', 'seup~pismeno']"
          [style]="{ height: '35rem' }"
        >
          <app-notification-items
            [notificationType]="['seup~predmet', 'seup~pismeno']"
          >
            <p>Nemate obavijesti o uredskom poslovanju</p>
          </app-notification-items>
        </app-homepage-segment>

        <app-homepage-segment
          class="col-6"
          title="NORMATIVNA BAZA OSOBA"
          [entityType]="['en~agent']"
          [style]="{ height: '35rem' }"
        >
          <app-notification-items [notificationType]="['en~agent']">
            <p>Nemate obavijesti o normativnoj bazi osoba</p>
          </app-notification-items>
        </app-homepage-segment>
        }
      </div>
    </main>
  `
})
export class HomePageComponent {
  private authService = inject(AuthService);

  public userFirstName$ = this.authService.validUser$.pipe(
    map((u) => u.firstName)
  );

  isUserVoditeljIliKorisnikUredskogPoslovanja(): Observable<boolean> {
    return this.authService.validUser$.pipe(
      map((user) => {
        return AuthService.userHasAnyRole(user, [
          'Voditelj uredskog poslovanja',
          'Korisnik uredskog poslovanja'
        ]);
      })
    );
  }
}
